import React, { useState, useRef, useEffect } from "react";
import { Button, Box, Modal } from "@mui/material";
import Cropper from "react-easy-crop";
import PropTypes from "prop-types";
import warning from "../../../assets/images/Warning.svg";
import checkImg from "../../../assets/images/check_circle.svg";
import closeIcon from "../../../assets/images/closeicon.svg";
import uploadFile from "../../../assets/images/uploadFile.svg";
import Camera from "../../../assets/images/Camera.png";
import cameraplus from "../../../assets/images/CameraPlus.png";
import cameraSlash from "../../../assets/images/CameraSlash.png";
import AccessibleTooltip from "../../Tooltip/Tooltip";
import {
  UploadProfilePhoto,
  ResetProfileState,
} from "../../../Actions/MyProfileActions";
import { useDispatch, useSelector } from "react-redux";
import "../../../assets/styles/glButton.scss";

import {
  handleFileUploadUtil,
  capturePhotoUtil,
  openCameraUtil,
  stopCameraUtil,
  resetPhotoUtil,
} from "../../../utils/updateProfilePhotoUtils.js";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  padding: 4,
};

const UpdateProfilePhoto = ({ setOpen, open, fetchImage }) => {
  const dispatch = useDispatch();

  // ✅ Local states
  const [formError, setFormError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [successMessage, setSuccessMessage] = useState(false);
  const [isFromCamera, setIsFromCamera] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const { photoUploadError, photoUploadSuccess } = useSelector(
    (state) => state.profile
  );

  const videoRef = useRef(null);

  useEffect(() => {
    return () => {
      dispatch(ResetProfileState());
    };
  }, [dispatch]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.key === "Escape" || e.key === "Esc") && tooltipOpen) {
        setTooltipOpen(false);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, tooltipOpen]);

  useEffect(() => {
    setTimeout(() => {
      document.getElementById("closeButtonRef")?.focus();
    }, 200);
  }, [open]);

  useEffect(() => {
    if (photoUploadSuccess) {
      setSuccessMessage(true);
      fetchImage();
    } else if (photoUploadError) {
      setFormError(photoUploadError);
    }
  }, [photoUploadError, photoUploadSuccess, fetchImage]);

  const handleFileUpload = (event) =>
    handleFileUploadUtil(event, setFormError, setUploadedFile, setImagePreview, setIsFromCamera);

  const handleCapturePhoto = () =>
    capturePhotoUtil(videoRef, stopCamera, setUploadedFile, setImagePreview, setIsFromCamera);

  const handleOpenCamera = () =>
    openCameraUtil(videoRef, setCameraMode, setFormError, setCameraStream);

  const stopCamera = () =>
    stopCameraUtil(cameraStream, setCameraStream, setCameraMode);

  const handleRetake = () => {
    resetPhotoUtil(setImagePreview, setUploadedFile, setCrop, setZoom, setRotation, setCroppedArea);
    handleOpenCamera();
  };

  const onCropComplete = (croppedAreaPercentage, croppedAreaPixels) =>
    setCroppedArea(croppedAreaPixels);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!uploadedFile) {
      setFormError("Please upload or capture an image.");
      return;
    }
    dispatch(UploadProfilePhoto(imagePreview, croppedArea, rotation));
  };

  const handleClose = () => {
    setOpen(false);
    setUploadedFile(null);
    setImagePreview(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedArea(null);
    setCameraMode(false);
    setSuccessMessage(false);
    setFormError("");
    setIsFromCamera(false);
    dispatch({ type: "RESET_PROFILE_STATE_PHOTO" });
    if (cameraStream) stopCamera();
  };

  const renderImagePreview = () => (
    <div className="image-preview-container">
      <Cropper
        image={imagePreview}
        crop={crop}
        zoom={zoom}
        aspect={1}
        onCropChange={setCrop}
        onZoomChange={setZoom}
        onCropComplete={onCropComplete}
        mediaProps={{
          tabIndex: -1,
          alt: "Profile photo",
        }}
      />
    </div>
  );

  const renderRetakeButton = () => (
    <div style={{ textAlign: "center", marginTop: "5px" }}>
      <Button
        variant="outlined"
        onClick={handleRetake}
        className="btn--capture"
        data-testid="retake-button"
        startIcon={<img src={cameraSlash} alt="" />}
        disableRipple
      >
        Retake
      </Button>
    </div>
  );

  useEffect(() => {
    if (successMessage)
      setTimeout(() => {
        document.getElementById("doneofphotoupload")?.focus();
      }, 200);
  }, [successMessage]);

  useEffect(() => {
    if (imagePreview !== "")
      setTimeout(() => {
        document.getElementById("afterPhotoupload")?.focus();
      }, 500);
  }, [imagePreview]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className="profileModal"
      disableEscapeKeyDown={tooltipOpen}
      data-testid="update-profile-photo"
    >
      <div role="none">
        <Box
          role="dialog"
          sx={style}
          aria-labelledby="upload-photo-modal-title"
          className="mainModalBox"
        >
          <div className="Modal_header">
            <h1 id="upload-photo-modal-title">Upload Photo</h1>
            <AccessibleTooltip title="Close" open={tooltipOpen}>
              <button
                id="closeButtonRef"
                onClick={handleClose}
                className="close_button_modal"
                aria-label="Close"
                onFocus={() => setTooltipOpen(true)}
                onBlur={() => setTooltipOpen(false)}
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
                style={{ padding: "5px", background: "transparent" }}
              >
                <img src={closeIcon} alt="" aria-hidden="true" />
              </button>
            </AccessibleTooltip>
          </div>

          {!successMessage ? (
            <>
              <div
                className="Modal_middle uploadMediaContainer"
                id="upload-photo-modal-description"
                role={`${!imagePreview && !cameraMode ? "list" : "none"}`}
              >
                {!cameraMode && (
                  <>
                    {!imagePreview && (
                      <>
                        <div
                          role="listitem"
                          className="uploadFileMiddle"
                          onClick={() =>
                            document.getElementById("file-input-upload").click()
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              document.getElementById("file-input-upload").click();
                            }
                          }}
                        >
                          <label
                            htmlFor="file-input-upload"
                            className="innerFields attachmentField"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="contained"
                              component="span"
                              className="attachmentBtn"
                              aria-label="Browse from your computer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <img src={uploadFile} alt="" aria-hidden="true" />
                            </Button>
                          </label>

                          <input
                            id="file-input-upload"
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleFileUpload}
                            data-testid="file-input"
                          />
                          <p className="inst">Browse from your computer</p>
                          <p className="type">(File type: .JPEG or .PNG)</p>
                          <p className="type">Max Size: 1MB</p>
                        </div>

                        <div role="listitem" className="uploadFileMiddle">
                          <div
                            className="innerFields attachmentField"
                            role="button"
                            aria-label="Camera"
                            tabIndex={0}
                            onClick={handleOpenCamera}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOpenCamera();
                              }
                            }}
                          >
                            <Button
                              variant="contained"
                              component="span"
                              className="attachmentBtn"
                              aria-label="Use camera to take photo"
                              tabIndex={-1}
                            >
                              <img src={Camera} alt="" aria-hidden="true" />
                            </Button>
                          </div>
                          <p className="inst" data-testid="camera-text">Camera</p>
                          <p className="inst">Live capture by Camera</p>
                        </div>
                      </>
                    )}
                    {imagePreview && renderImagePreview()}
                  </>
                )}

                {cameraMode && (
                  <div>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      style={{ width: "100%" }}
                      muted
                      title="Camera live preview"
                    />
                    <div style={{ textAlign: "center" }}>
                      <Button
                        variant="contained"
                        onClick={handleCapturePhoto}
                        className="btn--capture"
                        startIcon={<img src={cameraplus} alt="" aria-hidden="true" />}
                        id="capturePhotocamera"
                        aria-label="Capture photo"
                        disableRipple
                      >
                        Capture Photo
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {imagePreview && isFromCamera && renderRetakeButton()}

              {formError && (
                <p className="profileError" role="alert">
                  <img src={warning} alt="" aria-hidden="true" />
                  {formError}
                </p>
              )}

              <div className="Modal_footer">
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    id="afterPhotoupload"
                    onClick={handleSubmit}
                    className="containedButton"
                    aria-label="Save"
                  >
                    Save
                  </button>
                </Box>
              </div>
            </>
          ) : (
            <div className="sucssess_Box" role="status" aria-live="polite">
              <img src={checkImg} alt="" aria-hidden="true" />
              <h2>Success</h2>
              <p>Profile photo updated successfully.</p>
              <Button
                onClick={handleClose}
                variant="contained"
                className="btn-contained"
                aria-label="Done"
                id="doneofphotoupload"
                disableRipple
              >
                Done
              </Button>
            </div>
          )}
        </Box>
      </div>
    </Modal>
  );
};

UpdateProfilePhoto.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  fetchImage: PropTypes.func.isRequired,
};

export default UpdateProfilePhoto;
